import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import SearchField from 'components/inputs/SearchField';
import { H4 } from 'components/typography/Headers';
import NoSearchResult from 'components/ui-elements/NoSearchResult';
import Spinner from 'components/ui-elements/Spinner';
import {
  MAX_ADDRESS_LINE_LENGTH,
  ORGANIZATION_DEFAULT_COUNTRY,
} from 'features/my-profile/become-fundraiser/context/becomeFundraiserUtils';
import { OrganizationFormData } from 'features/my-profile/become-fundraiser/form/components/add-organization/useAddOrganizationForm';
import { OrganizationDto } from 'services/payment/paymentService.dto';

import OrganizationTile from './OrganizationTile';
import { useSearchOrganizations } from './useSearchOrganizations';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 395px;
  gap: 24px;
  max-height: 75vh;
  text-align: center;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
`;

const Header = styled(H4)`
  margin-bottom: 12px;

  ${theme.mq.phone} {
    margin-bottom: 0;
  }
`;

interface Props {
  onSelected: (organization: OrganizationFormData) => void;
}

const SearchOrganization = ({ onSelected }: Props) => {
  const { t } = useTranslation('myProfile');
  const [searchField, setSearchField] = useState('');
  const { organizations, isLoading } = useSearchOrganizations(searchField);

  const displayNoResult = !isLoading && !!searchField && organizations.length === 0;
  const displayData = organizations.length > 0;

  const handleOrganizationSelected = (organization: OrganizationDto) => {
    const { street, state, zip, city } = organization.address;
    const addressLine1 = street.substring(0, MAX_ADDRESS_LINE_LENGTH);
    const addressLine2 =
      street.length > MAX_ADDRESS_LINE_LENGTH
        ? street.substring(MAX_ADDRESS_LINE_LENGTH, 2 * MAX_ADDRESS_LINE_LENGTH)
        : '';

    const organizationFormData: OrganizationFormData = {
      name: organization.name,
      ein: organization.ein,
      state: state,
      zipCode: zip,
      city: city,
      country: ORGANIZATION_DEFAULT_COUNTRY,
      addressLine1: addressLine1,
      addressLine2: addressLine2,
      isNonProfit: true,
    };

    onSelected(organizationFormData);
  };

  return (
    <Container data-testid={'organization-search'}>
      <Header data-testid={'header'}>{t('become-fundraiser.verify-organization.search-organizations')}</Header>
      <SearchField
        onSubmit={setSearchField}
        onChange={setSearchField}
        placeholder={t('common:search')}
        autoFocus={true}
        testId={'search-field'}
      />
      <ListContainer>
        {isLoading && <Spinner />}
        {displayNoResult && <NoSearchResult />}
        {displayData &&
          organizations.map(organization => (
            <OrganizationTile
              key={organization.id}
              organization={organization}
              onSelected={() => handleOrganizationSelected(organization)}
            />
          ))}
      </ListContainer>
    </Container>
  );
};

export default SearchOrganization;
